/*
 *  -----------------------------------------------------------------------
 *    <copyright file="constants.ts" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { isRiskIqEnabled } from '@utils-clean/feature-flags';
import {
    DataReviewTableColumnType,
    PanelGraphCategoryClause,
    FieldDefinitionFieldType,
} from 'types/graphql-global-types';

const empty = '000000000000000000000000';

export enum ModuleNames {
    CM = 'CM',
    RAM = 'RAM',
    // The ISMP module has been deprecated however this value has been retained for audit log purposes.
    ISMP = 'ISMP',
    CM2 = 'CM2',
    SM = 'SM',
    DQ = 'DQ',
}

export type ModuleName = `${ModuleNames}`;

// These IDs match to the API enum which uses a bit flag in hexadecimal. New modules should always be a power of 2
// The order in this list also determines the order shown in the assign modules dialog.
const Modules: Record<ModuleName, number> = {
    CM: 1,
    CM2: 8,
    RAM: 2,
    // The ISMP module has been deprecated however this value has been retained for audit log purposes.
    ISMP: 4,
    SM: 16,
    DQ: 32,
};

export enum WidgetTypeCode {
    Static = 1,
    Calculated = 2,
}

export enum RowDataSourceTableEnum {
    Study = 1,
    Site = 2,
    SiteVisit = 3,
    SiteIssue = 4,
    SiteProtocolDeviation = 5,
    Subject = 6,
    SubjectAdverseEvent = 7,
    SubjectAssessment = 8,
    SubjectCrf = 9,
    SubjectInformedConsent = 10,
    SubjectIssue = 11,
    SubjectLab = 12,
    SubjectProtocolDeviation = 13,
    SubjectQuery = 14,
    SubjectVisit = 15,
    SubjectConcomitantMedication = 16,
    SubjectMedicalHistory = 17,
}

export enum WidgetIconEnum {
    Visit = 1,
    VisitExclamation = 2,
    VisitCross = 3,
    VisitCheck = 4,
    Subject = 5,
    SubjectTime = 6,
    SubjectCheck = 7,
    SubjectCross = 8,
    SubjectSlash = 9,
    SubjectOptions = 10,
    Patient = 11,
    SubjectGroup = 12,
    SubjectGroupSlash = 13,
    SubjectGroupOptions = 14,
    Site = 15,
    Assessment = 16,
    AssessmentCheck = 17,
    AssessmentMissing = 18,
    AssessmentExclamation = 19,
    AdverseEvent = 20,
    Heart = 21,
    HeartCross = 22,
    HeartCheck = 23,
    HeartExclamation = 24,
    Clock = 25,
    Calendar = 26,
    Hourglass = 27,
    Ambulance = 28,
    Observation = 29,
    Syringe = 30,
    Random = 31,
    Stethoscope = 32,
}

const PageElements = {
    RiskLog: 'page-risk-log',
    RAMManageCmControls: 'page-ram-manage-cm-controls',
    RiskLogComments: 'page-risk-log-comments',
    RAMManageObservations: 'page-ram-manage-observations',
    RAMManageActions: 'page-ram-manage-actions',
    RAMRiskIQ: 'page-ram-risk-iq',
    RAMRiskIQComments: 'page-ram-risk-iq-comments',
    RiskMonitoring: 'page-risk-monitoring',
    RiskReports: 'page-risk-reports',
    RiskIQ: 'page-ram-risk-iq',
    StudyTeam: 'page-ram-study-team',
    CriticalToQualityFactors: 'page-critical-to-quality-factors',
    CtqComments: 'page-ctq-comments',
    StudyDashboard: 'page-cm-study-dashboard',
    StudyObservations: 'page-study-observation',
    SiteObservations: 'page-site-observations',
    KRIOverview: 'page-kri-overview',
    StudyDatasetReview: 'page-study-dataset-review',
    SiteDatasetReview: 'page-site-dataset-review',
    SiteOverviews: 'page-cm-site-overviews',
    StudySummary: 'page-cm-study-summary',
    SiteDashboard: 'page-cm-site-dashboard',
    CMObservations: 'page-cm-manage-observations',
    CMActions: 'page-cm-manage-actions',
    CMStudyTeam: 'page-cm-study-team',
    SMStudyTeam: 'page-sm-study-team',
    CMReports: 'page-cm-reports',
    CMManageSignatories: 'page-cm-manage-signatories',
    CMGenerationHistory: 'page-cm-generation-history',
    DataViews: 'page-cm-data-views',
    ManageObservations: 'page-cm-manage-observations',
    ManageActions: 'page-cm-manage-actions',
    RiskDashboard: 'page-risk-dashboard',
    CMControls: 'page-ram-cm-controls',
    // CM2 pages
    StudyHubCM2: 'page-cm2-study-hub',
    SiteHubCM2: 'page-cm2-site-hub',
    DatasetHubCM2: 'page-cm2-dataset-hub',
    DataViewsCM2: 'page-cm2-data-views',
    StudyTeamCM2: 'page-cm2-study-team',
    CM2Reports: 'page-cm2-reports',
    MonitoringToolkit: 'page-cm2-monitoring-toolkit',
    ControlsHubCM2: 'page-cm2-controls-hub',
    // SM pages
    SubjectHubSM: 'page-sm-subject-hub',
    SubjectProfileSM: 'page-sm-subject-profile',
    SubjectMonitoringReports: 'page-sm-reports',
    DataQuality: 'page-dq',
    SubjectMonitoringToolkit: 'page-sm-monitoring-toolkit',
    SubjectMonitoringStudyTeam: 'page-sm-study-team',
    SubjectMonitoringManageReviewPlans: 'page-manage-review-plans',
};

const DataElements = {
    CentralMonitoring: 'data-cm',
    CentralMonitoringDefault: 'data-cm2',
    RiskAssessment: 'data-ram',
    RiskAssessmentRiskLogComments: 'data-ram-risk-log-comments',
    RiskAssessmentCtqComments: 'data-ram-ctq-comments',
    RiskAssessmentRiskIQ: 'data-ram-risk-iq',
    RiskAssessmentPiqComments: 'data-ram-risk-iq-comments',
    InternalRisks: 'data-internal-risks',
    ComplianceReport: 'data-ram-compliance-report',
    SubjectMonitoring: 'data-sm',
};

// RiskIQ permission entries are hidden while the RiskIQ UI feature flag is off. Existing role rights
// for hidden elements must be preserved (not dropped) when a role's permissions are saved.
const riskIqPermissionElements: string[] = [
    DataElements.RiskAssessmentRiskIQ,
    DataElements.RiskAssessmentPiqComments,
    PageElements.RAMRiskIQ,
    PageElements.RAMRiskIQComments,
];

const isFeatureHiddenPermission = (element: string | null | undefined) =>
    !isRiskIqEnabled() && !!element && riskIqPermissionElements.includes(element);

const Constants = {
    environment: {
        LOCAL: 'LOCAL',
        DEV: 'DEV',
        DEV1: 'DEV1',
        DEV2: 'DEV2',
        TEST: 'TEST',
        TEST1: 'TEST1',
        TEST2: 'TEST2',
        VALPREP: 'VALPREP',
        STUDYBUILD: 'STUDYBUILD',
        PROD: 'PROD',
    },
    CLOSE: 'close',
    CANCEL: 'cancel',
    OPEN: 'open',
    ObjectId: {
        Empty: empty,
    },
    clauses: [
        {
            id: PanelGraphCategoryClause.EQUAL,
            text: '=',
        },
        {
            id: PanelGraphCategoryClause.LESS_THAN,
            text: '<',
        },
        {
            id: PanelGraphCategoryClause.MORE_THAN,
            text: '>',
        },
        {
            id: PanelGraphCategoryClause.LESS_THAN_EQUAL,
            text: '<=',
        },
        {
            id: PanelGraphCategoryClause.MORE_THAN_EQUAL,
            text: '>=',
        },
    ],
    TRISecuritySettings: {
        minPasswordLength: 15, // Length of current seeded passwords
        minLowercaseCount: 0,
        minUppercaseCount: 0,
        minNumberCount: 0,
        minSpecialCharCount: 0,
        accountInactiveDays: 90,
        numAttemptsBeforeLockout: 3,
        lockoutPeriodMinutes: 10,
    },
    comparatorTypes: [
        {
            id: 'Field',
            text: 'Field',
        },
        {
            id: 'Aggregate',
            text: 'Aggregate',
        },
    ],
    conjunctions: [
        {
            id: 'AND',
            text: 'And',
        },
        {
            id: 'OR',
            text: 'Or',
        },
    ],
    aggregationTypes: [
        DataReviewTableColumnType.PERCENTAGE,
        DataReviewTableColumnType.RATE,
        DataReviewTableColumnType.MAX,
        DataReviewTableColumnType.MIN,
        DataReviewTableColumnType.SUM,
        DataReviewTableColumnType.COUNT,
    ],
    booleanSelector: [
        {
            id: 'true',
            text: 'Yes',
        },
        {
            id: 'false',
            text: 'No',
        },
    ],
    OrganisationTypes: ['CRO', 'SPONSOR'],
    DBRegions: ['OPRA_EU', 'OPRA_US', 'CUSTOM'],
    Modules,
    SystemAdminLevel: {
        None: 0,
        Reviewer: 1,
        Contributor: 2,
        Administrator: 3,
        SysAdmin: 4,
    },
    RagMapOptions: {
        RED: 0,
        AMBER: 1,
        GREEN: 2,
        MOVE: 3,
    },
    AuthenticationMethods: {
        OPRA: 0,
        SSO: 1,
    },
    Colors: {
        GREEN: '#417505',
        AMBER: '#f5a624',
        RED: '#d0021b',
        PURPLE: 'purple',
        EMPTY: 'black',
    },
    ColumnWidth: {
        default: 174,
    },
    SiteColors: {
        RED: 1,
        AMBER: 2,
        GREEN: 3,
    },
    PageElements,
    DataElements,
    FieldDefinitionFieldTypes: {
        TEXT: 0,
        DATE: 1,
        PARTIAL_DATE: 2,
        INT: 3,
        FLOAT: 4,
        BOOL: 5,
    },
    FieldDefinitionFieldTextTypes: {
        text: FieldDefinitionFieldType.TEXT,
        date: FieldDefinitionFieldType.DATE,
        partialDate: FieldDefinitionFieldType.PARTIAL_DATE,
        int: FieldDefinitionFieldType.INT,
        float: FieldDefinitionFieldType.FLOAT,
        bool: FieldDefinitionFieldType.BOOL,
    },
    DataReviewTableColumnType: [
        DataReviewTableColumnType.STATIC,
        DataReviewTableColumnType.STANDARD_CALCULATION,
        DataReviewTableColumnType.PERCENTAGE,
        DataReviewTableColumnType.RATE,
        DataReviewTableColumnType.MAX,
        DataReviewTableColumnType.MIN,
        DataReviewTableColumnType.SUM,
        DataReviewTableColumnType.COUNT,
        DataReviewTableColumnType.COUNTRY_MEAN,
        DataReviewTableColumnType.STUDY_MEAN,
        DataReviewTableColumnType.SITE_MEAN,
        DataReviewTableColumnType.COUNTRY_MEDIAN,
        DataReviewTableColumnType.STUDY_MEDIAN,
        DataReviewTableColumnType.SITE_MEDIAN,
        DataReviewTableColumnType.PERCENTILE,
    ],
    possiblePermissions: [
        {
            title: 'Site Dashboard',
            element: PageElements.SiteDashboard,
            possiblePermissions: 0,
            moduleId: Modules.CM,
        },
        {
            title: 'Study Dashboard',
            element: PageElements.StudyDashboard,
            possiblePermissions: 0,
            moduleId: Modules.CM,
        },
        {
            title: 'Manage Observations',
            element: PageElements.CMObservations,
            possiblePermissions: 0,
            moduleId: Modules.CM,
        },
        {
            title: 'Manage Actions',
            element: PageElements.CMActions,
            possiblePermissions: 0,
            moduleId: Modules.CM,
        },
        {
            title: 'Study Team',
            element: PageElements.CMStudyTeam,
            possiblePermissions: 0,
            moduleId: Modules.CM,
        },
        {
            title: 'Reports',
            element: PageElements.CMReports,
            possiblePermissions: 0,
            moduleId: Modules.CM,
        },
        {
            title: 'Controls Hub',
            element: PageElements.ControlsHubCM2,
            possiblePermissions: 0,
            moduleId: Modules.CM2,
        },
        {
            title: 'Study Hub',
            element: PageElements.StudyHubCM2,
            possiblePermissions: 0,
            moduleId: Modules.CM2,
        },
        {
            title: 'Site Hub',
            element: PageElements.SiteHubCM2,
            possiblePermissions: 0,
            moduleId: Modules.CM2,
        },
        {
            title: 'Dataset Hub',
            element: PageElements.DatasetHubCM2,
            possiblePermissions: 0,
            moduleId: Modules.CM2,
        },
        {
            title: 'Data Views',
            element: PageElements.DataViewsCM2,
            possiblePermissions: 0,
            moduleId: Modules.CM2,
        },
        {
            title: 'Study Team',
            element: PageElements.StudyTeamCM2,
            possiblePermissions: 0,
            moduleId: Modules.CM2,
        },
        {
            title: 'Reports',
            element: PageElements.CM2Reports,
            possiblePermissions: 0,
            moduleId: Modules.CM2,
        },
        {
            title: 'Monitoring Toolkit',
            element: PageElements.MonitoringToolkit,
            possiblePermissions: 0,
            moduleId: Modules.CM2,
        },
        {
            title: 'Risk Assessment',
            element: DataElements.RiskAssessment,
            possiblePermissions: 0,
            showHide: false,
            moduleId: Modules.RAM,
        },
        {
            title: 'Risk Assessment - Critical To Quality Factors Comments',
            element: DataElements.RiskAssessmentCtqComments,
            possiblePermissions: 0,
            showHide: false,
            moduleId: Modules.RAM,
        },
        {
            title: 'Risk Assessment - Risk Log Comments',
            element: DataElements.RiskAssessmentRiskLogComments,
            possiblePermissions: 0,
            showHide: false,
            moduleId: Modules.RAM,
        },
        {
            title: 'Risk Assessment - RiskIQ',
            element: DataElements.RiskAssessmentRiskIQ,
            possiblePermissions: 0,
            showHide: false,
            moduleId: Modules.RAM,
        },
        {
            title: 'Risk Assessment - RiskIQ Comments',
            element: DataElements.RiskAssessmentPiqComments,
            possiblePermissions: 0,
            showHide: false,
            moduleId: Modules.RAM,
        },
        {
            title: 'Central Monitoring (Legacy)',
            element: DataElements.CentralMonitoring,
            possiblePermissions: 0,
            showHide: false,
            moduleId: Modules.CM,
        },
        {
            title: 'Subject Monitoring',
            element: DataElements.SubjectMonitoring,
            possiblePermissions: 0,
            showHide: false,
            moduleId: Modules.SM,
        },
        {
            title: 'Monitoring Toolkit',
            element: PageElements.SubjectMonitoringToolkit,
            possiblePermissions: 0,
            moduleId: Modules.SM,
        },
        {
            title: 'Study Team',
            element: PageElements.SMStudyTeam,
            possiblePermissions: 0,
            moduleId: Modules.SM,
        },
        {
            title: 'Subject Hub',
            element: PageElements.SubjectHubSM,
            possiblePermissions: 0,
            moduleId: Modules.SM,
        },
        {
            title: 'Subject Profile',
            element: PageElements.SubjectProfileSM,
            possiblePermissions: 0,
            moduleId: Modules.SM,
        },
        {
            title: 'Reports',
            element: PageElements.SubjectMonitoringReports,
            possiblePermissions: 0,
            moduleId: Modules.SM,
        },
        {
            title: 'Manage Review Plans',
            element: PageElements.SubjectMonitoringManageReviewPlans,
            possiblePermissions: 0,
            moduleId: Modules.SM,
        },
        {
            title: 'Central Monitoring',
            element: DataElements.CentralMonitoringDefault,
            possiblePermissions: 0,
            showHide: false,
            moduleId: Modules.CM2,
        },
        {
            title: 'Internal Risks',
            element: DataElements.InternalRisks,
            possiblePermissions: 0,
            showHide: true,
            moduleId: Modules.RAM,
        },
        {
            title: 'Risk Assessment Compliance Report',
            element: DataElements.ComplianceReport,
            possiblePermissions: 0,
            showHide: true,
            moduleId: Modules.RAM,
            hidden: true,
        },
        {
            title: 'Critical To Quality Factors',
            element: PageElements.CriticalToQualityFactors,
            possiblePermissions: 0,
            moduleId: Modules.RAM,
        },
        {
            title: 'Critical To Quality Factors Comments',
            element: PageElements.CtqComments,
            possiblePermissions: 0,
            showHide: false,
            moduleId: Modules.RAM,
        },
        {
            title: 'Risk Log',
            element: PageElements.RiskLog,
            possiblePermissions: 0,
            moduleId: Modules.RAM,
        },
        {
            title: 'Risk Log Comments',
            element: PageElements.RiskLogComments,
            possiblePermissions: 0,
            moduleId: Modules.RAM,
        },
        {
            title: 'Risk Monitoring',
            element: PageElements.RiskMonitoring,
            possiblePermissions: 0,
            moduleId: Modules.RAM,
        },
        {
            title: 'Risk Dashboard',
            element: PageElements.RiskDashboard,
            possiblePermissions: 0,
            moduleId: Modules.RAM,
        },
        {
            title: 'Reports',
            element: PageElements.RiskReports,
            possiblePermissions: 0,
            moduleId: Modules.RAM,
        },
        {
            title: 'Study Team',
            element: PageElements.StudyTeam,
            possiblePermissions: 0,
            moduleId: Modules.RAM,
        },
        {
            title: 'CM Controls',
            element: PageElements.CMControls,
            possiblePermissions: 0,
            moduleId: Modules.RAM,
        },
        {
            title: 'RiskIQ',
            element: PageElements.RAMRiskIQ,
            possiblePermissions: 0,
            moduleId: Modules.RAM,
        },
        {
            title: 'RiskIQ Comments',
            element: PageElements.RAMRiskIQComments,
            possiblePermissions: 0,
            moduleId: Modules.RAM,
        },
        {
            title: 'Data Quality',
            element: PageElements.DataQuality,
            possiblePermissions: 0,
            moduleId: Modules.DQ,
        },
    ].filter((item) => !isFeatureHiddenPermission(item.element)),
    isFeatureHiddenPermission,
    filterOperators: {
        text: [
            { text: 'grid.filterContainsOperator', operator: 'contains' },
            { text: 'grid.filterNotContainsOperator', operator: 'doesnotcontain' },
            { text: 'grid.filterEqOperator', operator: 'eq' },
            { text: 'grid.filterNotEqOperator', operator: 'neq' },
            { text: 'grid.filterStartsWithOperator', operator: 'startswith' },
            { text: 'grid.filterEndsWithOperator', operator: 'endswith' },
            { text: 'grid.filterIsNullOperator', operator: 'isempty' },
            { text: 'grid.filterIsNotNullOperator', operator: 'isnotempty' },
        ],
        numeric: [
            { text: 'grid.filterEqOperator', operator: 'eq' },
            { text: 'grid.filterNotEqOperator', operator: 'neq' },
            { text: 'grid.filterGteOperator', operator: 'gte' },
            { text: 'grid.filterGtOperator', operator: 'gt' },
            { text: 'grid.filterLteOperator', operator: 'lte' },
            { text: 'grid.filterLtOperator', operator: 'lt' },
            { text: 'grid.filterIsNullOperator', operator: 'isnull' },
            { text: 'grid.filterIsNotNullOperator', operator: 'isnotnull' },
        ],
        date: [
            { text: 'grid.filterEqOperator', operator: 'eq' },
            { text: 'grid.filterNotEqOperator', operator: 'neq' },
            { text: 'grid.filterAfterOrEqualOperator', operator: 'gte' },
            { text: 'grid.filterAfterOperator', operator: 'gt' },
            { text: 'grid.filterBeforeOperator', operator: 'lt' },
            { text: 'grid.filterBeforeOrEqualOperator', operator: 'lte' },
            { text: 'grid.filterIsNullOperator', operator: 'isnull' },
            { text: 'grid.filterIsNotNullOperator', operator: 'isnotnull' },
        ],
        boolean: [{ text: 'grid.filterEqOperator', operator: 'eq' }],
    },
    moduleName: {
        // These IDs match to the API enum which uses a bit flag in hexadecimal. New modules should always be a power of 2
        1: 'Central Monitoring Core (Legacy)',
        2: 'Risk Assessment Core',
        // The ISMP module has been deprecated. The module ID has been retained for audit log purposes however we no
        // longer display the full module name anywhere.
        // 4: 'Risk Assessment Integrated Strategic Monitoring Plan (ISMP)'
        8: 'Central Monitoring Core',
        16: 'Subject Monitoring',
        32: 'Data Quality',
    } as { [key: string]: any },
    scheduleDialogTypes: {
        addScheduleGroup: 'addScheduleGroup',
        addScheduleVersion: 'addScheduleVersion',
        editScheduleGroup: 'editScheduleGroup',
        editScheduleVersion: 'editScheduleVersion',
    },
    EvaluationColour: ['Red', 'Amber', 'Green'],
    DirectionStatuses: ['L', 'U'],
    Reporting: {
        ReportTemplateStatus: {
            draft: 1,
            publishing: 2,
            failedToPublish: 3,
            published: 4,
            retired: 5,
        },
        reportingDialogConfirmation: {
            retire: 'retire',
            publish: 'publish',
        },
    },
    widgets: {
        IconsList: [
            { key: 'Visit', value: WidgetIconEnum.Visit },
            { key: 'Visit Exclamation', value: WidgetIconEnum.VisitExclamation },
            { key: 'Visit Cross', value: WidgetIconEnum.VisitCross },
            { key: 'Visit Check', value: WidgetIconEnum.VisitCheck },
            { key: 'Subject', value: WidgetIconEnum.Subject },
            { key: 'Subject Time', value: WidgetIconEnum.SubjectTime },
            { key: 'Subject Check', value: WidgetIconEnum.SubjectCheck },
            { key: 'Subject Cross', value: WidgetIconEnum.SubjectCross },
            { key: 'Subject Slash', value: WidgetIconEnum.SubjectSlash },
            { key: 'Subject Options', value: WidgetIconEnum.SubjectOptions },
            { key: 'Patient', value: WidgetIconEnum.Patient },
            { key: 'Subject Group', value: WidgetIconEnum.SubjectGroup },
            { key: 'Subject Group Slash', value: WidgetIconEnum.SubjectGroupSlash },
            { key: 'Subject Group Options', value: WidgetIconEnum.SubjectGroupOptions },
            { key: 'Site', value: WidgetIconEnum.Site },
            { key: 'Assessment', value: WidgetIconEnum.Assessment },
            { key: 'Assessment Check', value: WidgetIconEnum.AssessmentCheck },
            { key: 'Assessment Missing', value: WidgetIconEnum.AssessmentMissing },
            { key: 'Assessment Exclamation', value: WidgetIconEnum.AssessmentExclamation },
            { key: 'Adverse Event', value: WidgetIconEnum.AdverseEvent },
            { key: 'Heart', value: WidgetIconEnum.Heart },
            { key: 'Heart Cross', value: WidgetIconEnum.HeartCross },
            { key: 'Heart Check', value: WidgetIconEnum.HeartCheck },
            { key: 'Heart Exclamation', value: WidgetIconEnum.HeartExclamation },
            { key: 'Clock', value: WidgetIconEnum.Clock },
            { key: 'Calendar', value: WidgetIconEnum.Calendar },
            { key: 'Hourglass', value: WidgetIconEnum.Hourglass },
            { key: 'Ambulance', value: WidgetIconEnum.Ambulance },
            { key: 'Observation', value: WidgetIconEnum.Observation },
            { key: 'Syringe', value: WidgetIconEnum.Syringe },
            { key: 'Random', value: WidgetIconEnum.Random },
            { key: 'Stethoscope', value: WidgetIconEnum.Stethoscope },
        ],
    },
    DataSourceLOV: [
        { key: 'Study', value: RowDataSourceTableEnum.Study, code: 'STUDY' },
        { key: 'Site', value: RowDataSourceTableEnum.Site, code: 'SITE' },
        { key: 'Site Visit', value: RowDataSourceTableEnum.SiteVisit, code: 'SITE_VISIT' },
        { key: 'Site Issue', value: RowDataSourceTableEnum.SiteIssue, code: 'SITE_ISSUE' },
        {
            key: 'Site Protocol Deviation',
            value: RowDataSourceTableEnum.SiteProtocolDeviation,
            code: 'SITE_PROTOCOL_DEVIATION',
        },
        { key: 'Subject', value: RowDataSourceTableEnum.Subject, code: 'SUBJECT' },
        {
            key: 'Subject Adverse Event',
            value: RowDataSourceTableEnum.SubjectAdverseEvent,
            code: 'SUBJECT_ADVERSE_EVENT',
        },
        { key: 'Subject Assessment', value: RowDataSourceTableEnum.SubjectAssessment, code: 'SUBJECT_ASSESSMENT' },
        { key: 'Subject Crf', value: RowDataSourceTableEnum.SubjectCrf, code: 'SUBJECT_CRF' },
        {
            key: 'Subject Informed Consent',
            value: RowDataSourceTableEnum.SubjectInformedConsent,
            code: 'SUBJECT_INFORMED_CONSENT',
        },
        { key: 'Subject Issue', value: RowDataSourceTableEnum.SubjectIssue, code: 'SUBJECT_ISSUE' },
        { key: 'Subject Lab', value: RowDataSourceTableEnum.SubjectLab, code: 'SUBJECT_LAB' },
        {
            key: 'Subject Protocol Deviation',
            value: RowDataSourceTableEnum.SubjectProtocolDeviation,
            code: 'SUBJECT_PROTOCOL_DEVIATION',
        },
        { key: 'Subject Query', value: RowDataSourceTableEnum.SubjectQuery, code: 'SUBJECT_QUERY' },
        { key: 'Subject Visit', value: RowDataSourceTableEnum.SubjectVisit, code: 'SUBJECT_VISIT' },
        {
            key: 'Subject Concomitant Medication',
            value: RowDataSourceTableEnum.SubjectConcomitantMedication,
            code: 'SUBJECT_CONCOMITANT_MEDICATION',
        },
        {
            key: 'Subject Medical History',
            value: RowDataSourceTableEnum.SubjectMedicalHistory,
            code: 'SUBJECT_MEDICAL_HISTORY',
        },
    ],
};

export enum ReportStatusNames {
    Draft = 'Draft',
    Publishing = 'Publishing',
    FailedToPublish = 'FailedToPublish',
    SuccessfullyPublished = 'SuccessfullyPublished',
    Retired = 'Retired',
}

export enum GeneratedReportStatusNames {
    Queued = 'Queued',
    Ready = 'Ready',
    Failed = 'Failed',
    Obsolete = 'Obsolete',
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export enum MODAL_CLOSE_ACTIONS {
    ERROR = 'error',
    SUCCESS = 'success',
    UNASSIGN = 'unassign',
    SUCCESS_AND_CLOSE = 'success-and-close',
}

export enum SplitterOrientation {
    horizontal = 'horizontal',
    vertical = 'vertical',
}

export enum GridFullView {
    full = 'full',
}

export enum RadialChartObsActions {
    Open = 'Open',
    Closed = 'Closed',
}

export enum RadialChartKris {
    Reviewed = 'Reviewed',
    Unreviewed = 'Unreviewed',
}
export type GridViewOptions = SplitterOrientation | GridFullView;

export default Constants;
